import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { FiEye, FiList, FiUpload, FiPlayCircle, FiLogOut, FiMenu, FiX } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const font = "'Outfit', sans-serif";
const C = { bg: '#fdf4ff', sidebar: '#4a044e', text: '#1a0020', white: '#fff', border: '#e9d5ff', accent: '#a855f7' };

const InvigilatorLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout: authLogout } = useAuth();
    const token = sessionStorage.getItem('exam_portal_token');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto-close sidebar when route changes on mobile
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    if (!token || !user || user.role !== 'invigilator') {
        return <Navigate to="/login" replace />;
    }

    const logout = () => { authLogout(); navigate('/login'); };

    const links = [
        { path: '/invigilator/dashboard', icon: <FiEye size={18} />, label: 'My Room' },
        { path: '/invigilator/seat-sheet', icon: <FiList size={18} />, label: 'Seat Sheet' },
        { path: '/invigilator/upload-answers', icon: <FiUpload size={18} />, label: 'Upload Answer Sheets' },
    ];

    return (
        <div style={{ display: 'flex', height: '100vh', width: '100vw', background: C.bg, fontFamily: font, overflow: 'hidden', position: 'relative' }}>
            
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div 
                    onClick={() => setSidebarOpen(false)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 40, display: isMobile ? 'block' : 'none' }}
                />
            )}

            {/* Sidebar */}
            <div style={{ 
                width: 260, background: C.sidebar, color: '#fff', display: 'flex', flexDirection: 'column', padding: '24px 0',
                position: isMobile ? 'fixed' : 'relative',
                top: 0, bottom: 0, left: isMobile ? (sidebarOpen ? 0 : -260) : 0,
                zIndex: 50, transition: 'left 0.3s ease', boxShadow: sidebarOpen ? '4px 0 24px rgba(0,0,0,0.2)' : 'none'
            }}>
                <div style={{ padding: '0 24px', marginBottom: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #c084fc, #a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(168,85,247,0.4)' }}>
                            <FiPlayCircle size={22} />
                        </div>
                        <div>
                            <h2 style={{ fontSize: 15, fontWeight: 900, margin: 0 }}>SVU Exam</h2>
                            <span style={{ fontSize: 10, color: '#d8b4fe', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Invigilator</span>
                        </div>
                    </div>
                    {/* Close button for mobile */}
                    <button 
                        onClick={() => setSidebarOpen(false)}
                        style={{ display: isMobile ? 'flex' : 'none', background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    >
                        <FiX size={18} />
                    </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 16px', flex: 1 }}>
                    {links.map(l => {
                        const act = location.pathname.startsWith(l.path);
                        return (
                            <button key={l.path} onClick={() => navigate(l.path)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', background: act ? 'rgba(255,255,255,0.12)' : 'transparent', border: 'none', borderRadius: 12, color: act ? '#fff' : '#e9d5ff', fontSize: 13, fontWeight: act ? 800 : 600, cursor: 'pointer', fontFamily: font, textAlign: 'left', transition: 'all 0.15s' }}>
                                <span style={{ opacity: act ? 1 : 0.7 }}>{l.icon}</span>
                                {l.label}
                                {act && <div style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: '#c084fc' }} />}
                            </button>
                        );
                    })}
                </div>

                <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#7e22ce', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800 }}>{user.name?.charAt(0) || 'I'}</div>
                        <div><div style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>{user.name}</div><div style={{ fontSize: 10, color: '#d8b4fe' }}>{user.username}</div></div>
                    </div>
                    <button onClick={logout} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '9px', background: 'rgba(239,68,68,0.12)', color: '#fca5a5', border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: font }}>
                        <FiLogOut size={13} /> Logout
                    </button>
                </div>
            </div>

        {/* Content Area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', width: isMobile ? '100vw' : 'calc(100vw - 260px)' }}>
            
            {/* Mobile Header with Hamburger */}
            <div style={{ 
                display: isMobile ? 'flex' : 'none', 
                alignItems: 'center', padding: '16px 24px', background: C.white, borderBottom: `1px solid ${C.border}`,
                boxShadow: '0 2px 10px rgba(0,0,0,0.02)', zIndex: 10 
            }}>
                <button 
                    onClick={() => setSidebarOpen(true)}
                    style={{ background: 'none', border: 'none', color: C.text, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, marginRight: 16 }}
                >
                    <FiMenu size={24} />
                </button>
                <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>SVU Invigilator</div>
            </div>

            {/* Main Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '20px 16px' : '30px 40px' }}>
                <Outlet />
            </div>
        </div>
        </div>
    );
};

export default InvigilatorLayout;
